import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadingIcon from '@mui/icons-material/Downloading';
import InfoIcon from '@mui/icons-material/Info';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import {
    Box,
    Button,
    Card,
    CardContent,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    Grid,
    IconButton,
    Switch,
    TextField,
    Typography,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import React, { useCallback, useMemo, useState } from 'react';
import { Pie } from 'react-chartjs-2';
import MoneyInputWithBlur from '../MoneyInputWithBlur';
import PercentInputWithBlur from '../PercentInputWithBlur';
import TimeInputWithBlur from '../TimeInputWithBlur';
import PublicCalculatorPlansCta from '../front/PublicCalculatorPlansCta';
import { publicColors, publicEditorialBannerSx, publicEditorialBodySx, publicEditorialTitleSx } from '../front/publicTheme';
import PageTutorialButtons from '../tutorials/PageTutorialButtons';
import { downloadCalculatorPdf } from '../../helpers/calculatorPdf';
import { formatCurrencyInFloat } from '../../helpers/formatters';
import {
    calculatePriceQuote,
    createCalculatorPart,
    getDefaultCalculatorInputs,
    resolveCalculatorQuantity,
    saveCalculatorParts,
    saveCalculatorPreference,
    CALCULATOR_STORAGE_KEYS,
} from '../../helpers/priceCalculator';

/** Cores fixas por fatia do gráfico (sem repetição). */
const PRICE_SLICE_COLORS = {
    Energia: '#F97316',
    Material: '#16A34A',
    Depreciação: '#92400E',
    'Taxa de falhas': '#DC2626',
    Embalagem: '#9333EA',
    Outros: '#171717',
    'Taxa marketplace': '#EAB308',
    'Taxa maquininha': '#C084FC',
    'Imposto PJ': '#0D9488',
};

function introToHtml(intro) {
    if (typeof intro !== 'string' || !intro.trim()) {
        return '';
    }
    return intro.replace(/\r\n/g, '\n').replace(/\n/g, '<br />');
}

function editorialBannerSx(variant, theme) {
    const layout = {
        width: '100%',
        minHeight: { xs: 140, sm: 156 },
        px: { xs: 2.5, md: 3.25 },
        py: { xs: 2, md: 2.25 },
        borderRadius: 2,
        display: 'flex',
        alignItems: 'center',
        boxSizing: 'border-box',
        mb: 4,
    };

    if (variant === 'public') {
        return { ...publicEditorialBannerSx, mb: 4 };
    }

    const isDark = theme.palette.mode === 'dark';
    return {
        ...layout,
        bgcolor: isDark
            ? alpha(theme.palette.primary.main, 0.16)
            : alpha(theme.palette.primary.main, 0.08),
        color: theme.palette.text.primary,
        border: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.38 : 0.22)}`,
        borderLeft: `4px solid ${theme.palette.primary.main}`,
    };
}

function editorialTitleSx(variant, theme) {
    if (variant === 'public') {
        return publicEditorialTitleSx;
    }

    return {
        fontWeight: 700,
        fontSize: { xs: '1.2rem', sm: '1.45rem', md: '1.7rem' },
        letterSpacing: '-0.04em',
        lineHeight: 1.12,
        mb: 0.75,
        color: theme.palette.text.primary,
        textTransform: 'uppercase',
        width: '100%',
    };
}

function editorialBodySx(variant, theme) {
    if (variant === 'public') {
        return publicEditorialBodySx;
    }

    return {
        fontWeight: 500,
        fontSize: { xs: '0.92rem', sm: '1rem' },
        letterSpacing: '-0.01em',
        lineHeight: 1.55,
        color: theme.palette.text.secondary,
        width: '100%',
    };
}

function editorialHowtoButtonSx(variant, theme) {
    if (variant === 'public') {
        return {
            mt: 1.5,
            alignSelf: 'flex-start',
            textTransform: 'none',
            fontWeight: 600,
            color: publicColors.primary,
            borderColor: alpha(publicColors.primary, 0.45),
            '&:hover': {
                borderColor: publicColors.primary,
                bgcolor: alpha(publicColors.primary, 0.08),
            },
        };
    }

    return {
        mt: 1.5,
        alignSelf: 'flex-start',
        textTransform: 'none',
        fontWeight: 600,
        color: theme.palette.primary.main,
        borderColor: alpha(theme.palette.primary.main, 0.45),
        '&:hover': {
            borderColor: theme.palette.primary.main,
            bgcolor: alpha(theme.palette.primary.main, 0.08),
        },
    };
}

function sectionTitleSx(variant, theme) {
    if (variant === 'public') {
        return { color: publicColors.ink, fontWeight: 700, mb: 2, mt: 3 };
    }
    return {
        color: theme.palette.mode === 'dark' ? '#90caf9' : '#3867d6',
        fontWeight: 700,
        mb: 2,
        mt: 3,
    };
}

function primaryButtonSx(variant, theme) {
    if (variant === 'public') {
        return {
            bgcolor: publicColors.primary,
            color: '#fff',
            px: 4,
            py: 1.5,
            fontWeight: 600,
            borderRadius: 1,
            textTransform: 'none',
            boxShadow: 'none',
            '&:hover': { bgcolor: publicColors.primaryDark, boxShadow: 'none' },
        };
    }
    return {
        bgcolor: theme.palette.primary.main,
        textTransform: 'none',
        fontWeight: 600,
        boxShadow: 'none',
    };
}

function CostPieChart({ slices, total, title, variant, theme }) {
    const accent = variant === 'public' ? publicColors.primary : theme.palette.primary.main;
    const data = {
        labels: slices.map((s) => s.label),
        datasets: [
            {
                data: slices.map((s) => s.value),
                backgroundColor: slices.map((s) => PRICE_SLICE_COLORS[s.label] || '#94A3B8'),
                borderColor: variant === 'public' ? publicColors.surface : theme.palette.background.paper,
                borderWidth: 2,
            },
        ],
    };

    return (
        <Box sx={{ height: 360, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Pie
                data={data}
                options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        title: {
                            display: true,
                            text: title,
                            font: { size: 16, weight: 'bold' },
                            color: variant === 'public' ? publicColors.ink : accent,
                        },
                        legend: { position: 'bottom' },
                        tooltip: {
                            callbacks: {
                                label: (ctx) => {
                                    const v = ctx.parsed;
                                    const pct = total > 0 ? ((v / total) * 100).toFixed(1) : '0';
                                    return `${ctx.label}: ${formatCurrencyInFloat(v)} (${pct}%)`;
                                },
                            },
                        },
                    },
                }}
            />
        </Box>
    );
}

function DetailRow({
    label,
    value,
    emphasize = false,
    profitPercent,
    unitCaption = '',
    note = '',
    highlightTotal = false,
    variant = 'admin',
}) {
    const hasProfitLine =
        profitPercent !== undefined && profitPercent !== null && !Number.isNaN(Number(profitPercent));
    const profitLabel = hasProfitLine
        ? `${Number(profitPercent).toFixed(1).replace('.', ',')}% de lucro`
        : null;
    const hasUnitLine = Boolean(unitCaption);
    const stacked = hasProfitLine || hasUnitLine;
    const totalAccent = variant === 'public' ? publicColors.primary : 'primary.main';

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: stacked ? 'flex-start' : 'baseline',
                gap: 0.75,
                py: highlightTotal ? 0.85 : 0.65,
                minWidth: 0,
            }}
        >
            <Box sx={{ flexShrink: 0, maxWidth: stacked ? { xs: '46%', md: '42%' } : '48%', pt: stacked ? 0.15 : 0 }}>
                <Typography
                    variant={highlightTotal ? 'body1' : 'body2'}
                    color={highlightTotal ? totalAccent : 'text.secondary'}
                    sx={{ lineHeight: 1.45, fontWeight: highlightTotal ? 700 : 400 }}
                >
                    {label}
                </Typography>
                {note ? (
                    <Typography variant="caption" color="text.secondary" component="div" sx={{ lineHeight: 1.3, mt: 0.25 }}>
                        {note}
                    </Typography>
                ) : null}
            </Box>
            <Box
                component="span"
                aria-hidden
                sx={{
                    flex: 1,
                    borderBottom: '1px dotted',
                    borderColor: 'divider',
                    opacity: 0.85,
                    minWidth: 12,
                    alignSelf: stacked ? 'flex-start' : 'flex-end',
                    mt: stacked ? 1.15 : 0,
                    mb: stacked ? 0 : 0.4,
                }}
            />
            <Box sx={{ flexShrink: 0, textAlign: 'right', minWidth: 0 }}>
                <Typography
                    variant={highlightTotal ? 'body1' : 'body2'}
                    component="div"
                    color={highlightTotal ? totalAccent : 'text.primary'}
                    sx={{
                        fontWeight: highlightTotal ? 800 : emphasize ? 700 : 600,
                        fontVariantNumeric: 'tabular-nums',
                        lineHeight: 1.35,
                    }}
                >
                    {value}
                </Typography>
                {hasUnitLine ? (
                    <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.35, lineHeight: 1.3 }}>
                        {unitCaption}
                    </Typography>
                ) : null}
                {hasProfitLine && (
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        component="div"
                        sx={{ mt: 0.35, lineHeight: 1.3, whiteSpace: 'nowrap' }}
                    >
                        {profitLabel}
                    </Typography>
                )}
            </Box>
        </Box>
    );
}

function filamentRowLabel(line) {
    if (!line?.name) {
        return 'Filamento';
    }
    if (String(line.name).startsWith('Filamento')) {
        return line.name;
    }
    return `Filamento · ${line.name}`;
}

function filamentRowNote(line) {
    if (!line || !(line.grams > 0)) {
        return '';
    }
    const grams = Number(line.grams).toLocaleString('pt-BR', { maximumFractionDigits: 1 });
    return `${grams} g × ${formatCurrencyInFloat(line.costPerKilogram)}/kg`;
}

function unitPriceCaption(amount, quantity, exactUnit) {
    const qty = resolveCalculatorQuantity(quantity);
    if (qty <= 1) {
        return '';
    }
    const unit = exactUnit != null ? exactUnit : amount / qty;
    return `${formatCurrencyInFloat(unit)} por unidade`;
}

function QuoteBreakdown({ quote, variant = 'admin' }) {
    const productionRows = useMemo(() => {
        const quantity = quote.quantity;
        const filamentRows = (quote.filamentLines || [])
            .filter((line) => line.cost > 0)
            .map((line, index) => ({
                key: `filament-${index}`,
                label: filamentRowLabel(line),
                amount: line.cost,
                note: filamentRowNote(line),
                unitCaption: unitPriceCaption(line.cost, quantity),
            }));
        const rows = [
            { key: 'energy', label: 'Energia (luz)', amount: quote.energy },
            ...filamentRows,
            { key: 'depreciation', label: 'Depreciação da impressora', amount: quote.depreciation },
            {
                key: 'packaging',
                label: 'Embalagem',
                amount: quote.packaging,
                unitCaption: unitPriceCaption(quote.packaging, quantity, quote.packagingUnit),
            },
            {
                key: 'other',
                label: 'Outros custos',
                amount: quote.other,
            },
            { key: 'rework', label: 'Taxa de falhas', amount: quote.reworkReserve },
        ].filter((row) => row.amount > 0);

        if (quote.marketplaceFeeOnList > 0) {
            rows.push({
                key: 'marketplace',
                label: 'Taxa de marketplace',
                amount: quote.marketplaceFeeOnList,
                unitCaption: unitPriceCaption(quote.marketplaceFeeOnList, quantity, quote.marketplaceFeeUnit),
            });
        }
        if (quote.cardFeeOnList > 0) {
            rows.push({
                key: 'card',
                label: 'Taxa da maquininha',
                amount: quote.cardFeeOnList,
                unitCaption: unitPriceCaption(quote.cardFeeOnList, quantity, quote.cardFeeUnit),
            });
        }
        if (quote.pjTaxFeeOnList > 0) {
            rows.push({
                key: 'pj',
                label: 'Imposto PJ',
                amount: quote.pjTaxFeeOnList,
                unitCaption: unitPriceCaption(quote.pjTaxFeeOnList, quantity, quote.pjTaxFeeUnit),
            });
        }

        const hasFees =
            quote.marketplaceFeeOnList > 0 || quote.cardFeeOnList > 0 || quote.pjTaxFeeOnList > 0;
        const total = hasFees
            ? quote.productionCost + quote.marketplaceFeeOnList + quote.cardFeeOnList + quote.pjTaxFeeOnList
            : quote.productionCost;
        rows.push({
            key: 'total',
            label: hasFees ? 'Total de custos' : 'Total de produção',
            amount: total,
            emphasize: true,
            unitCaption: unitPriceCaption(total, quantity),
        });
        return rows.map((row) => {
            if (row.key === 'rework' || row.unitCaption) {
                return row;
            }
            return { ...row, unitCaption: unitPriceCaption(row.amount, quantity) };
        });
    }, [quote]);

    const saleRows = useMemo(
        () => [
            {
                key: 'suggested',
                label: 'Sugestão de venda',
                amount: quote.suggestedListPrice,
                profitPercent: quote.marginOnSuggested,
                unitCaption: unitPriceCaption(quote.suggestedListPrice, quote.quantity),
            },
            {
                key: 'desired',
                label: 'Valor desejado',
                amount: quote.salePrice,
                profitPercent: quote.marginOnSale,
                unitCaption: unitPriceCaption(quote.salePrice, quote.quantity),
            },
        ],
        [quote],
    );

    return (
        <Box>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: 'stretch',
                }}
            >
                <Box sx={{ flex: 1, minWidth: 0, pr: { md: 3 } }}>
                    <Typography
                        variant="overline"
                        color="text.secondary"
                        sx={{ fontWeight: 700, letterSpacing: 0.06, display: 'block', mb: 1.25 }}
                    >
                        Custos da impressão
                    </Typography>
                    {productionRows.map((row) => (
                        <Box
                            key={row.key}
                            sx={
                                row.emphasize
                                    ? { pt: 1.25, mt: 0.5, borderTop: 1, borderColor: 'divider' }
                                    : undefined
                            }
                        >
                            <DetailRow
                                label={row.label}
                                value={formatCurrencyInFloat(row.amount)}
                                emphasize={row.emphasize}
                                highlightTotal={row.emphasize}
                                note={row.note}
                                unitCaption={row.unitCaption}
                                variant={variant}
                            />
                        </Box>
                    ))}
                </Box>

                <Divider
                    orientation="vertical"
                    flexItem
                    sx={{
                        display: { xs: 'none', md: 'block' },
                        mx: 0,
                        borderColor: 'divider',
                        opacity: 0.9,
                    }}
                />
                <Divider sx={{ display: { xs: 'block', md: 'none' }, my: 2.5 }} />

                <Box sx={{ flex: 1, minWidth: 0, pl: { md: 3 } }}>
                    <Typography
                        variant="overline"
                        color="text.secondary"
                        sx={{ fontWeight: 700, letterSpacing: 0.06, display: 'block', mb: 1.25 }}
                    >
                        Resumo
                    </Typography>
                    <Box sx={{ py: 0.5 }}>
                        {saleRows.map((row) => (
                            <Box key={row.key} sx={{ mb: 2.5 }}>
                                <DetailRow
                                    label={row.label}
                                    value={formatCurrencyInFloat(row.amount)}
                                    emphasize
                                    profitPercent={row.profitPercent}
                                    unitCaption={row.unitCaption}
                                    variant={variant}
                                />
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}

export default function PriceCalculatorPanel({ variant = 'admin', intro, introTitle }) {
    const theme = useTheme();
    const [inputs, setInputs] = useState(() => getDefaultCalculatorInputs());
    const [quote, setQuote] = useState(() => calculatePriceQuote(getDefaultCalculatorInputs()));
    const [kwhInfoOpen, setKwhInfoOpen] = useState(false);
    const [usefulLifeInfoOpen, setUsefulLifeInfoOpen] = useState(false);
    const [reworkInfoOpen, setReworkInfoOpen] = useState(false);
    const [salePriceInfoOpen, setSalePriceInfoOpen] = useState(false);
    const [downloadingPdf, setDownloadingPdf] = useState(false);

    const persist = useCallback((key, value) => {
        saveCalculatorPreference(key, value);
    }, []);

    const runCalculate = useCallback((nextInputs) => {
        const source = nextInputs || inputs;
        setQuote(calculatePriceQuote(source));
    }, [inputs]);

    const update = useCallback((patch) => {
        setInputs((prev) => {
            const next = { ...prev, ...patch };
            if (patch.parts) {
                saveCalculatorParts(patch.parts);
            }
            runCalculate(next);
            return next;
        });
    }, [runCalculate]);

    const addPart = () => {
        const previous = inputs.parts[inputs.parts.length - 1];
        const next = [...inputs.parts, createCalculatorPart({
            name: `Peça ${inputs.parts.length + 1}`,
            grams: 0,
            costPerKilogram: previous?.costPerKilogram ?? inputs.costPerKilogram ?? '99.90',
        })];
        update({ parts: next });
    };

    const removePart = (id) => {
        if (inputs.parts.length <= 1) {
            return;
        }
        update({ parts: inputs.parts.filter((p) => p.id !== id) });
    };

    const updatePart = (id, field, value) => {
        update({
            parts: inputs.parts.map((p) => (p.id === id ? { ...p, [field]: value } : p)),
        });
    };

    const handleDownloadPdf = () => {
        setDownloadingPdf(true);
        try {
            const latestQuote = calculatePriceQuote(inputs);
            setQuote(latestQuote);
            downloadCalculatorPdf({ inputs, quote: latestQuote, variant });
        } finally {
            window.setTimeout(() => setDownloadingPdf(false), 400);
        }
    };

    const pieSlices = useMemo(() => {
        const slices = [
            { label: 'Energia', value: quote.energy },
            { label: 'Material', value: quote.material },
            { label: 'Depreciação', value: quote.depreciation },
            { label: 'Embalagem', value: quote.packaging },
            { label: 'Outros', value: quote.other },
            { label: 'Taxa de falhas', value: quote.reworkReserve },
        ].filter((s) => s.value > 0);

        if (quote.marketplaceFeeOnList > 0.0001) {
            slices.push({ label: 'Taxa marketplace', value: quote.marketplaceFeeOnList });
        }
        if (quote.cardFeeOnList > 0.0001) {
            slices.push({ label: 'Taxa maquininha', value: quote.cardFeeOnList });
        }
        if (quote.pjTaxFeeOnList > 0.0001) {
            slices.push({ label: 'Imposto PJ', value: quote.pjTaxFeeOnList });
        }
        return slices;
    }, [quote]);

    const pieChartTotal = useMemo(
        () => pieSlices.reduce((sum, slice) => sum + slice.value, 0),
        [pieSlices],
    );

    const pieChartTitle = useMemo(() => {
        const hasFees =
            quote.marketplaceFeeOnList > 0 ||
            quote.cardFeeOnList > 0 ||
            quote.pjTaxFeeOnList > 0;
        const totalLabel = formatCurrencyInFloat(hasFees ? pieChartTotal : quote.productionCost);
        const perUnit = unitPriceCaption(hasFees ? pieChartTotal : quote.productionCost, quote.quantity);
        const headline = hasFees ? `Custos e taxas: ${totalLabel}` : `Custo de produção: ${totalLabel}`;
        return perUnit ? [headline, perUnit] : headline;
    }, [quote, pieChartTotal]);

    const depreciationHint = useMemo(() => {
        const value = parseFloat(inputs.printerValue) || 0;
        const life = parseFloat(inputs.printerUsefulLifeHours) || 0;
        if (value <= 0 || life <= 0) {
            return null;
        }
        return `${formatCurrencyInFloat(value)} ÷ ${life.toLocaleString('pt-BR')} h = ${formatCurrencyInFloat(quote.costPerHour)}/h`;
    }, [inputs.printerValue, inputs.printerUsefulLifeHours, quote.costPerHour]);

    const cardSx =
        variant === 'public'
            ? {
                bgcolor: publicColors.surface,
                border: `1px solid ${publicColors.border}`,
                borderRadius: 2,
                boxShadow: 'none',
            }
            : {
                borderRadius: 2,
            };

    const resultPrimaryCard =
        variant === 'public'
            ? {
                ...cardSx,
                bgcolor: publicColors.primarySoft,
                border: `1px solid ${publicColors.primary}`,
                color: publicColors.ink,
            }
            : { borderRadius: 2, bgcolor: theme.palette.primary.main, color: '#fff', boxShadow: 'none' };

    const resultAccentCard =
        variant === 'public'
            ? {
                ...cardSx,
                bgcolor: publicColors.accentSoft,
                border: `1px solid ${publicColors.accent}`,
                color: publicColors.ink,
            }
            : { borderRadius: 2, bgcolor: '#0d9488', color: '#fff', boxShadow: 'none' };

    const resultCardContentBase = {
        textAlign: 'center',
        '& .result-label': { mb: 1 },
        '& .result-value': { fontWeight: 800 },
        '& .result-caption': { display: 'block', mt: 1 },
    };

    const resultCardContentSx =
        variant === 'public'
            ? {
                ...resultCardContentBase,
                '& .result-label': { color: publicColors.inkMuted, mb: 1 },
                '& .result-value': { color: publicColors.primary },
                '& .result-caption': { color: publicColors.inkMuted },
            }
            : {
                ...resultCardContentBase,
                '& .result-label': { opacity: 0.9 },
                '& .result-caption': { opacity: 0.85 },
            };

    const resultAccentCardContentSx =
        variant === 'public'
            ? {
                ...resultCardContentBase,
                '& .result-label': { color: publicColors.inkMuted, mb: 1 },
                '& .result-value': { color: publicColors.accent },
                '& .result-caption': { color: publicColors.inkMuted },
            }
            : resultCardContentSx;

    return (
        <Box>
            {intro || introTitle ? (
                <Box sx={editorialBannerSx(variant, theme)}>
                    <Box sx={{ minWidth: 0, width: '100%', display: 'flex', flexDirection: 'column' }}>
                        {introTitle ? (
                            <Typography sx={editorialTitleSx(variant, theme)}>
                                {introTitle}
                            </Typography>
                        ) : null}
                        {intro ? (
                            <Typography
                                sx={editorialBodySx(variant, theme)}
                                dangerouslySetInnerHTML={{ __html: introToHtml(intro) }}
                            />
                        ) : null}
                        <PageTutorialButtons
                            pageKey="calculator"
                            buttonLabel="Veja como funciona"
                            size="small"
                            sx={editorialHowtoButtonSx(variant, theme)}
                        />
                    </Box>
                </Box>
            ) : null}

            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Typography variant="h6" sx={sectionTitleSx(variant, theme)}>
                        Produto
                    </Typography>
                </Grid>
                <Grid item xs={12} sm={8}>
                    <TextField
                        label="Nome do produto"
                        fullWidth
                        value={inputs.productName}
                        onChange={(e) => {
                            update({ productName: e.target.value });
                            persist(CALCULATOR_STORAGE_KEYS.productName, e.target.value);
                        }}
                        placeholder="Ex.: Campo Futebol Jogo da Velha"
                        helperText="Usado no título e no nome do arquivo PDF"
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <TextField
                        label="Quantidade"
                        fullWidth
                        type="number"
                        value={inputs.quantity}
                        onChange={(e) => {
                            const quantity = e.target.value;
                            update({ quantity });
                            persist(CALCULATOR_STORAGE_KEYS.quantity, quantity);
                        }}
                        onBlur={() => runCalculate()}
                        inputProps={{ min: 1, step: 1 }}
                        helperText="Unidades desta impressão"
                    />
                </Grid>

                <Grid item xs={12}>
                    <Typography variant="h6" sx={sectionTitleSx(variant, theme)}>
                        Impressora
                    </Typography>
                </Grid>
                {!inputs.multipartEnabled && (
                    <>
                        <Grid item xs={12} sm={4}>
                            <TimeInputWithBlur
                                label="Tempo de impressão (HH:mm)"
                                value={inputs.printTime}
                                onChange={(e) => update({ printTime: e.target.value })}
                                onBlur={() => runCalculate()}
                            />
                        </Grid>
                        <Grid item xs={6} sm={4}>
                            <TextField
                                label="Consumo do filamento (g)"
                                fullWidth
                                type="number"
                                value={inputs.usedMaterial}
                                onChange={(e) => update({ usedMaterial: e.target.value })}
                                onBlur={() => runCalculate()}
                                inputMode="decimal"
                            />
                        </Grid>
                        <Grid item xs={6} sm={4}>
                            <MoneyInputWithBlur
                                label="Custo por kg"
                                value={inputs.costPerKilogram}
                                onChange={(values) => {
                                    update({ costPerKilogram: values.value });
                                    persist(CALCULATOR_STORAGE_KEYS.costPerKilogram, values.value);
                                }}
                                onBlur={() => runCalculate()}
                            />
                        </Grid>
                    </>
                )}
                <Grid item xs={12} sm={inputs.multipartEnabled ? 6 : 4}>
                    <TextField
                        label="Consumo da impressora (W)"
                        fullWidth
                        type="number"
                        value={inputs.consumption}
                        onChange={(e) => {
                            update({ consumption: e.target.value });
                            persist(CALCULATOR_STORAGE_KEYS.consumption, e.target.value);
                        }}
                        onBlur={() => runCalculate()}
                        inputMode="numeric"
                    />
                </Grid>
                <Grid item xs={12} sm={inputs.multipartEnabled ? 6 : 4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <MoneyInputWithBlur
                            label="Custo do kWh na sua região"
                            value={inputs.energyCost}
                            onChange={(values) => {
                                update({ energyCost: values.value });
                                persist(CALCULATOR_STORAGE_KEYS.energyCost, values.value);
                            }}
                            onBlur={() => runCalculate()}
                        />
                        <IconButton
                            size="small"
                            onClick={() => setKwhInfoOpen(true)}
                            color="primary"
                            aria-label="Informações sobre custo do kWh"
                        >
                            <InfoIcon />
                        </IconButton>
                    </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                    <MoneyInputWithBlur
                        label="Valor da impressora"
                        value={inputs.printerValue}
                        onChange={(values) => {
                            update({ printerValue: values.value });
                            persist(CALCULATOR_STORAGE_KEYS.printerValue, values.value);
                        }}
                        onBlur={() => runCalculate()}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TextField
                            label="Vida útil estimada (horas)"
                            fullWidth
                            type="number"
                            value={inputs.printerUsefulLifeHours}
                            onChange={(e) => {
                                update({ printerUsefulLifeHours: e.target.value });
                                persist(CALCULATOR_STORAGE_KEYS.printerUsefulLifeHours, e.target.value);
                            }}
                            onBlur={() => runCalculate()}
                            inputMode="numeric"
                            helperText={
                                depreciationHint
                                    ? `Depreciação: ${depreciationHint}`
                                    : 'Ex.: R$ 5.000 em 5.000 h'
                            }
                        />
                        <IconButton
                            size="small"
                            onClick={() => setUsefulLifeInfoOpen(true)}
                            color="primary"
                            aria-label="Informações sobre vida útil da impressora"
                        >
                            <InfoIcon />
                        </IconButton>
                    </Box>
                </Grid>

                <Grid item xs={12}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 2,
                            mt: 1,
                        }}
                    >
                        <Typography variant="h6" sx={{ ...sectionTitleSx(variant, theme), mt: 0, mb: 0 }}>
                            Impressão multiparte
                        </Typography>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={inputs.multipartEnabled}
                                    onChange={(e) => {
                                        const enabled = e.target.checked;
                                        persist(CALCULATOR_STORAGE_KEYS.multipartEnabled, enabled);
                                        update({ multipartEnabled: enabled });
                                    }}
                                    color="primary"
                                />
                            }
                            label={inputs.multipartEnabled ? 'Produto contém diversas partes' : 'Produto contém apenas uma parte'}
                        />
                    </Box>
                </Grid>

                {inputs.multipartEnabled && (
                    <Grid item xs={12}>
                        <Box
                            sx={{
                                p: 2,
                                borderRadius: 2,
                                border: `1px solid ${variant === 'public' ? publicColors.border : theme.palette.divider}`,
                                bgcolor: variant === 'public' ? publicColors.surfaceAlt : 'background.paper',
                            }}
                        >
                            {inputs.parts.map((part, index) => (
                                <Grid container spacing={2} key={part.id} sx={{ mb: index < inputs.parts.length - 1 ? 2 : 0 }} alignItems="center">
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            label={`Peça ${index + 1} — nome (opcional)`}
                                            fullWidth
                                            value={part.name}
                                            onChange={(e) => updatePart(part.id, 'name', e.target.value)}
                                            onBlur={() => runCalculate()}
                                        />
                                    </Grid>
                                    <Grid item xs={6} sm={2}>
                                        <TimeInputWithBlur
                                            label="Tempo (HH:mm)"
                                            value={part.time}
                                            onChange={(e) => updatePart(part.id, 'time', e.target.value)}
                                            onBlur={() => runCalculate()}
                                        />
                                    </Grid>
                                    <Grid item xs={6} sm={2}>
                                        <TextField
                                            label="Consumo (g)"
                                            fullWidth
                                            type="number"
                                            value={part.grams}
                                            onChange={(e) => updatePart(part.id, 'grams', e.target.value)}
                                            onBlur={() => runCalculate()}
                                            inputMode="decimal"
                                        />
                                    </Grid>
                                    <Grid item xs={10} sm={3}>
                                        <MoneyInputWithBlur
                                            label="Custo por kg"
                                            value={part.costPerKilogram}
                                            onChange={(values) => updatePart(part.id, 'costPerKilogram', values.value)}
                                            onBlur={() => runCalculate()}
                                        />
                                    </Grid>
                                    <Grid item xs={2} sm={1} sx={{ display: 'flex', alignItems: 'center' }}>
                                        <IconButton
                                            aria-label="remover peça"
                                            onClick={() => removePart(part.id)}
                                            disabled={inputs.parts.length <= 1}
                                            color="error"
                                        >
                                            <DeleteOutlineIcon />
                                        </IconButton>
                                    </Grid>
                                </Grid>
                            ))}
                            <Button startIcon={<AddIcon />} onClick={addPart} sx={{ mt: 2 }} variant="outlined" size="small">
                                Adicionar peça
                            </Button>
                            <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                                Total: {Math.floor(quote.totalMinutes / 60)}h
                                {String(quote.totalMinutes % 60).padStart(2, '0')}min · {quote.totalGrams.toFixed(0)} g
                            </Typography>
                        </Box>
                    </Grid>
                )}

                <Grid item xs={12}>
                    <Typography variant="h6" sx={sectionTitleSx(variant, theme)}>
                        Custos extras
                    </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <MoneyInputWithBlur
                        label="Embalagem por unidade"
                        value={inputs.packagingCost}
                        onChange={(values) => {
                            update({ packagingCost: values.value });
                            persist(CALCULATOR_STORAGE_KEYS.packagingCost, values.value);
                        }}
                        onBlur={() => runCalculate()}
                        helperText="Valor de uma unidade, multiplicado pela quantidade"
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <MoneyInputWithBlur
                        label="Outros (montagem, pintura, etc.)"
                        value={inputs.otherCosts}
                        onChange={(values) => update({ otherCosts: values.value })}
                        onBlur={() => runCalculate()}
                        helperText="Valor da impressão inteira"
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PercentInputWithBlur
                            label="Taxa de falhas (%)"
                            value={inputs.reworkPercent}
                            onChange={(values) => {
                                update({ reworkPercent: values.value });
                                persist(CALCULATOR_STORAGE_KEYS.reworkPercent, values.value);
                            }}
                            onBlur={() => runCalculate()}
                            helperText="Sobre a impressão, sem a embalagem"
                        />
                        <IconButton
                            size="small"
                            onClick={() => setReworkInfoOpen(true)}
                            color="primary"
                            aria-label="Informações sobre taxa de falhas"
                        >
                            <InfoIcon />
                        </IconButton>
                    </Box>
                </Grid>

                <Grid item xs={12}>
                    <Typography variant="h6" sx={sectionTitleSx(variant, theme)}>
                        Taxas na venda
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Marketplace, maquininha e imposto PJ incidem sobre o preço de cada unidade e entram no total
                        multiplicados pela quantidade. O preço sugerido já considera o repasse dessas taxas.
                    </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <PercentInputWithBlur
                        label="Taxa marketplace (%)"
                        value={inputs.marketplacePercent}
                        onChange={(values) => {
                            update({ marketplacePercent: values.value });
                            persist(CALCULATOR_STORAGE_KEYS.marketplacePercent, values.value);
                        }}
                        onBlur={() => runCalculate()}
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <PercentInputWithBlur
                        label="Taxa maquininha (%)"
                        value={inputs.cardFeePercent}
                        onChange={(values) => {
                            update({ cardFeePercent: values.value });
                            persist(CALCULATOR_STORAGE_KEYS.cardFeePercent, values.value);
                        }}
                        onBlur={() => runCalculate()}
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <PercentInputWithBlur
                        label="Imposto PJ (%)"
                        value={inputs.pjTaxPercent}
                        onChange={(values) => {
                            update({ pjTaxPercent: values.value });
                            persist(CALCULATOR_STORAGE_KEYS.pjTaxPercent, values.value);
                        }}
                        onBlur={() => runCalculate()}
                    />
                </Grid>

                <Grid item xs={12}>
                    <Typography variant="h6" sx={sectionTitleSx(variant, theme)}>
                        Margem desejada
                    </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <PercentInputWithBlur
                        label="Markup sobre o custo (%)"
                        value={inputs.markup}
                        onChange={(values) => {
                            update({ markup: values.value });
                            persist(CALCULATOR_STORAGE_KEYS.markup, values.value);
                        }}
                        onBlur={() => runCalculate()}
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <MoneyInputWithBlur
                            label="Valor desejado de venda"
                            value={inputs.salePrice}
                            onChange={(values) => update({ salePrice: values.value })}
                            onBlur={() => runCalculate()}
                            helperText="Total da tiragem, não o valor de uma unidade"
                        />
                        <IconButton
                            size="small"
                            onClick={() => setSalePriceInfoOpen(true)}
                            color="primary"
                            aria-label="Informações sobre valor desejado de venda"
                        >
                            <InfoIcon />
                        </IconButton>
                    </Box>
                </Grid>
            </Grid>

            {variant === 'public' ? <PublicCalculatorPlansCta /> : null}

            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <Button
                    variant="contained"
                    size="large"
                    onClick={() => {
                        runCalculate();
                        document.getElementById('calculator-results')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                    sx={primaryButtonSx(variant, theme)}
                >
                    Calcular preço
                </Button>
            </Box>

            <Box id="calculator-results" sx={{ mt: 2 }}>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 2,
                        mb: 2,
                    }}
                >
                    <Typography variant="h5" sx={{ ...sectionTitleSx(variant, theme), textAlign: 'center', m: 0 }}>
                        Resultado
                    </Typography>
                    <Button
                        variant="outlined"
                        size="medium"
                        startIcon={downloadingPdf ? <DownloadingIcon /> : <PictureAsPdfIcon />}
                        onClick={handleDownloadPdf}
                        disabled={downloadingPdf}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            borderColor: variant === 'public' ? publicColors.primary : undefined,
                            color: variant === 'public' ? publicColors.primary : undefined,
                        }}
                    >
                        {downloadingPdf ? 'Gerando PDF...' : 'Baixar PDF'}
                    </Button>
                </Box>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                            {String(inputs.productName || '').trim()
                                ? `Custo calculado para ${String(inputs.productName).trim()} · ${quote.quantity} ${quote.quantity === 1 ? 'unidade' : 'unidades'}`
                                : `Custo calculado para ${quote.quantity} ${quote.quantity === 1 ? 'unidade' : 'unidades'}`}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={5}>
                        <Card sx={cardSx}>
                            <CardContent>
                                <CostPieChart
                                    slices={
                                        pieSlices.length
                                            ? pieSlices
                                            : [{ label: 'Sem custo', value: 0.01 }]
                                    }
                                    total={pieSlices.length ? pieChartTotal : quote.productionCost}
                                    title={pieChartTitle}
                                    variant={variant}
                                    theme={theme}
                                />
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={7}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <Card sx={resultPrimaryCard}>
                                    <CardContent sx={resultCardContentSx}>
                                        <Typography variant="subtitle2" className="result-label">
                                            Sugestão de venda
                                        </Typography>
                                        <Typography variant="h4" className="result-value">
                                            {formatCurrencyInFloat(quote.suggestedListPrice)}
                                        </Typography>
                                        {unitPriceCaption(quote.suggestedListPrice, quote.quantity) ? (
                                            <Typography variant="body2" className="result-caption">
                                                {unitPriceCaption(quote.suggestedListPrice, quote.quantity)}
                                            </Typography>
                                        ) : null}
                                        <Typography variant="caption" className="result-caption">
                                            {quote.marginOnSuggested.toFixed(1).replace('.', ',')}% de lucro
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Card sx={resultAccentCard}>
                                    <CardContent sx={resultAccentCardContentSx}>
                                        <Typography variant="subtitle2" className="result-label">
                                            Valor desejado
                                        </Typography>
                                        <Typography variant="h4" className="result-value">
                                            {formatCurrencyInFloat(quote.salePrice)}
                                        </Typography>
                                        {unitPriceCaption(quote.salePrice, quote.quantity) ? (
                                            <Typography variant="body2" className="result-caption">
                                                {unitPriceCaption(quote.salePrice, quote.quantity)}
                                            </Typography>
                                        ) : null}
                                        <Typography variant="caption" className="result-caption">
                                            {quote.marginOnSale.toFixed(1).replace('.', ',')}% de lucro
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid item xs={12}>
                                <Card sx={cardSx}>
                                    <CardContent>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
                                            Detalhamento
                                        </Typography>
                                        <QuoteBreakdown quote={quote} variant={variant} />
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Box>

            <Dialog open={salePriceInfoOpen} onClose={() => setSalePriceInfoOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Valor desejado de venda</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary">
                        Informe o preço que você já tem em mente para cobrar nessa peça — ou o valor que um concorrente
                        está praticando. A calculadora compara com a sugestão de venda e mostra quanto de lucro real
                        sobra depois das taxas e do custo de produção.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSalePriceInfoOpen(false)}>Fechar</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={kwhInfoOpen} onClose={() => setKwhInfoOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Custo do kWh</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary">
                        Use o valor da sua conta de luz (R$/kWh). A energia da impressão é estimada pelo consumo em watts
                        e pelo tempo total das peças.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setKwhInfoOpen(false)}>Fechar</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={reworkInfoOpen} onClose={() => setReworkInfoOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Taxa de falhas</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" paragraph>
                        Nem toda impressão sai perfeita na primeira tentativa. Warping, aderência, entupimento,
                        queda de energia ou peça solta no meio do job geram falhas — e cada falha consome filamento,
                        energia e tempo de máquina de novo.
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                        A taxa de falhas reserva uma margem em cima do custo de produção para cobrir essas
                        repetições. Com 10%, você assume que, em média, cerca de 10% do que gastaria em material e
                        impressão pode ser “perdido” em tentativas extras — sem precisar tirar isso do seu bolso quando
                        a falha acontecer.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Ajuste conforme sua experiência: quem está começando ou imprime peças difíceis pode usar um
                        percentual maior; quem já tem processo estável e impressora calibrada pode reduzir. O importante
                        é não precificar como se 100% das impressões fossem certeiras.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setReworkInfoOpen(false)}>Fechar</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={usefulLifeInfoOpen} onClose={() => setUsefulLifeInfoOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Vida útil da impressora</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" paragraph>
                        Assim como qualquer máquina, a impressora 3D possui vida útil. Depois de ultrapassar muito esse
                        limite em horas de uso, as manutenções ficam mais elevadas e a produtividade é reduzida.
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                        Impressoras consideradas de uso casual (impressoras de entrada de todas as marcas) costumam ter vida útil estimada entre 5.000 e 10.000
                        horas de impressão — use esse intervalo como referência se não souber por onde começar.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        A depreciação divide o valor da impressora pela vida útil em horas de impressão. Ex.: R$ 5.000
                        ÷ 5.000 h = R$ 1,00 por hora ligada imprimindo — esse valor entra no custo de produção.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setUsefulLifeInfoOpen(false)}>Fechar</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
